import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Check, X, Crown } from 'lucide-react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useCurrentOrgRole } from '../../hooks/useCurrentOrgRole';
import BillingService from '../../services/billing.service';
import useTiers from '../../hooks/billing/useTiers';
import useAccountTier from '../../hooks/billing/useAccountTier';
import TierCatalogGrid from './components/TierCatalogGrid';
import { runEnterpriseCheckout, canManageEnterprisePlan } from './enterpriseCheckout';
import subscriptionsHeroBanner from '../../assets/subscriptions-hero-banner.png';
import './components/HeroBanner.css';
import './SubscriptionView.css';

const SubscriptionView = ({ onManage }) => {
  const { t } = useTranslation();
  const { currentAccount } = useOrganization();
  const accountId = currentAccount?.id ?? null;

  // Only an org admin/owner gets the self-serve enterprise pay CTA. The
  // org endpoints don't carry the user's role, so we derive it from the
  // current user's memberships (see useCurrentOrgRole). The backend
  // returns the role lowercased ("admin"/"owner"), so normalize before
  // comparing against the canonical uppercase set.
  const { role: currentOrgRole } = useCurrentOrgRole(
    currentAccount?.organization_id ?? null
  );
  const canManageEnterprise = canManageEnterprisePlan(currentOrgRole);

  // Catalog + current tier come straight from billing_service.
  const {
    data: tiers,
    loading: tiersLoading,
    error: tiersError,
  } = useTiers();

  const { data: currentTier, loading: currentTierLoading } =
    useAccountTier(accountId);

  // The public catalog (GET /tiers) always returns displayPrice "Custom"
  // for ENTERPRISE (it has no org context). The per-account endpoint
  // (GET /accounts/{id}/tier, via currentTier) DOES carry the org's
  // negotiated price. For the card that is the current ENTERPRISE tier we
  // override its displayPrice with the account tier's.
  //
  // Avoid the "Custom → $1,500/mo" flash: while the account tier is still
  // loading we don't yet know the org price, so we blank the ENTERPRISE
  // card's displayPrice (renders a neutral placeholder) instead of showing
  // the catalog's "Custom" and then swapping it. Once currentTier resolves
  // we show the real price.
  const gridTiers = useMemo(() => {
    if (!tiers) return tiers;
    return tiers.map((t) => {
      if (t.type !== 'ENTERPRISE') return t;
      if (currentTier?.type === 'ENTERPRISE' && currentTier?.displayPrice) {
        // Real org price is in — use it.
        return { ...t, displayPrice: currentTier.displayPrice };
      }
      if (currentTierLoading) {
        // Price not resolved yet — suppress "Custom" to avoid the flash.
        return { ...t, displayPrice: null, _pricePending: true };
      }
      return t; // not our enterprise account (or no price) — keep catalog value
    });
  }, [tiers, currentTier, currentTierLoading]);

  // Display-only — never sent to the backend. createSubscriptionCheckout
  // takes only (accountId, tierType); billing cycle has no effect on the
  // checkout call, only on which of monthlyPriceUsd/yearlyPriceUsd each
  // card shows.
  const [billingCycle, setBillingCycle] = useState('monthly');

  // Real per-tier savings, not a hardcoded "20%" — computed from the two
  // prices every tier already carries, so the badge never claims a
  // number the catalog doesn't back up.
  const savingsPercent = useMemo(() => {
    if (!tiers) return null;
    const rates = tiers
      .filter((t) => t.monthlyPriceUsd > 0 && t.yearlyPriceUsd > 0)
      .map((t) => 1 - t.yearlyPriceUsd / 12 / t.monthlyPriceUsd);
    if (rates.length === 0) return null;
    return Math.round(Math.max(...rates) * 100);
  }, [tiers]);

  // Antes navegaba a /user/wallet, que era otra entrada de menú. Ahora el plan y
  // el saldo son dos pestañas de lo mismo, así que "gestionar" es cambiar de
  // pestaña — lo decide quien monta la vista.
  const handleManageClick = () => onManage?.();

  // Enterprise self-serve pay path (org admin/owner). Delegates to the
  // pure runEnterpriseCheckout helper (unit-tested) with the real
  // BillingService + a window redirect; the spinner is driven by
  // checkoutTierId just like the other tier CTAs.
  const handleEnterpriseSubscribe = async (tier) => {
    if (!tier?.id) return;
    setCheckoutTierId(tier.id);
    try {
      await runEnterpriseCheckout({
        accountId,
        createSubscriptionCheckout: BillingService.createSubscriptionCheckout,
        clearStalePendingSubscription,
        redirect: (url) => {
          window.location.href = url;
        },
        openContactDialog,
        tier,
      });
    } finally {
      setCheckoutTierId(null);
    }
  };

  // The backend never expires a `pending` subscription record on its
  // own (confirmed: `expireAt` comes back null) — if a checkout is
  // started and abandoned (user closes the Tilopay tab, backs out,
  // etc.), the record stays pending forever and blocks every future
  // checkout attempt with 409 DUPLICATE_SUBSCRIPTION. We can't detect
  // the abandonment itself (window.location.href fully navigates away
  // from the SPA), so instead we clear a stale pending in two places:
  // once when this view mounts (covers "left and came back later"),
  // and again right before starting a new checkout (covers "left and
  // immediately clicked Switch plan again without reloading"). A
  // pending is only ever auto-canceled if it's for a DIFFERENT tier
  // than the account's real current tier — if it matches, it might be
  // seconds away from confirming, so we leave it alone.
  const clearStalePendingSubscription = useCallback(async () => {
    if (!accountId) return;
    const statusRes = await BillingService.getSubscriptionStatus(accountId);
    const subscription = statusRes.success ? statusRes.data?.subscription : null;
    if (
      subscription?.status === 'pending' &&
      subscription.tierType !== currentTier?.type
    ) {
      await BillingService.cancelSubscription(accountId);
    }
  }, [accountId, currentTier]);

  useEffect(() => {
    clearStalePendingSubscription();
    // Only re-run when the account changes — not on every currentTier
    // refetch, which would otherwise fire this on every render once
    // useAccountTier's data settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // The contact dialog backs both the Enterprise CTA and the
  // "Switch plan" CTA. `mode` controls the title and pre-filled
  // message; the form itself is the same.
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [contactMode, setContactMode] = useState('enterprise');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState('');

  const openContactDialog = (mode, tier = null) => {
    setContactMode(mode);
    if (mode === 'switch' && tier) {
      setMessage(
        `I'd like to switch to the ${tier.displayName} plan (${tier.type}).`
      );
    } else if (mode === 'enterprise' && tier) {
      setMessage(
        `I'm interested in the ${tier.displayName} plan and would like to discuss pricing and onboarding.`
      );
    } else {
      setMessage('');
    }
    setShowContactDialog(true);
  };

  // Tracks which tier's checkout is in flight so its CTA can show a
  // spinner instead of looking unresponsive — the same pattern
  // PackageCard uses for "Redirecting…" while createCheckoutSession
  // resolves. There are two awaited calls before the redirect
  // (clearStalePendingSubscription, then createSubscriptionCheckout),
  // so without this the button just sits there during both.
  const [checkoutTierId, setCheckoutTierId] = useState(null);

  const handleTierAction = async (mode, tier) => {
    if (!accountId || !tier?.type) {
      openContactDialog(mode, tier);
      return;
    }
    setCheckoutTierId(tier.id);
    try {
      // Best-effort — clears a pending subscription abandoned since the
      // mount-time check ran (e.g. user backed out of Tilopay and
      // immediately clicked again without reloading the page).
      await clearStalePendingSubscription();
      const res = await BillingService.createSubscriptionCheckout(accountId, tier.type);
      if (res.success && res.data?.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
        return;
      }
      // No self-serve checkout path for this tier (e.g. Enterprise) —
      // fall back to the existing contact dialog, in the same mode the
      // click would have opened before this change.
      openContactDialog(mode, tier);
    } finally {
      setCheckoutTierId(null);
    }
  };

  const handleContactClick = (tier) => handleTierAction('enterprise', tier);
  const handleSwitchClick = (tier) => handleTierAction('switch', tier);

  const closeContactDialog = () => {
    setShowContactDialog(false);
    setMessage('');
  };

  const handleSendContact = async () => {
    if (!email.trim() || !message.trim()) return;

    try {
      setLoading(true);
      // Self-serve plan switching has no public endpoint today (admin-
      // only). Until a sales-flow integration lands, the form simulates
      // a 2s send and shows a success toast — the actual delivery
      // wiring belongs to product/CRM.
      await new Promise((resolve) => setTimeout(resolve, 2000));

      setNotificationType('success');
      setShowNotification(true);

      setTimeout(() => {
        setShowNotification(false);
        setShowContactDialog(false);
        setMessage('');
      }, 2000);
    } catch (error) {
      setNotificationType('error');
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 2000);
    } finally {
      setLoading(false);
    }
  };

  const dialogTitle =
    contactMode === 'switch'
      ? 'Switch your plan'
      : t('subscriptionView.dialog.title');

  const dialogPlaceholder =
    contactMode === 'switch'
      ? 'Tell us which plan you want and any questions about migration.'
      : t('subscriptionView.dialog.placeholderMessage');

  return (
    <div className="subscription-view">
      <header className="wallet-banner subscription-view__hero">
        <img
          src={subscriptionsHeroBanner}
          alt=""
          aria-hidden="true"
          className="wallet-banner__image"
        />
        <div className="wallet-banner__text subscription-view__hero-text">
          <span className="subscription-view__eyebrow">SUBSCRIPTIONS</span>
          <h1>
            Choose the right plan to scale your{' '}
            <span className="subscription-view__hero-accent">AI operation</span>
          </h1>
          <p>
            Unlock more power, higher limits, and premium features as your
            needs grow.
          </p>
        </div>
      </header>

      <div className="subscription-view__toggle-row">
        <p className="subscription-view__toggle-hint">
          All plans include Zenit AI Lab core platform and continuous
          updates.
        </p>
        <div className="subscription-view__toggle" role="group" aria-label="Billing cycle">
          <button
            type="button"
            className={`subscription-view__toggle-btn${
              billingCycle === 'monthly' ? ' subscription-view__toggle-btn--active' : ''
            }`}
            onClick={() => setBillingCycle('monthly')}
            aria-pressed={billingCycle === 'monthly'}
          >
            Monthly
          </button>
          <button
            type="button"
            className={`subscription-view__toggle-btn${
              billingCycle === 'yearly' ? ' subscription-view__toggle-btn--active' : ''
            }`}
            onClick={() => setBillingCycle('yearly')}
            aria-pressed={billingCycle === 'yearly'}
          >
            Yearly
          </button>
          {savingsPercent != null && savingsPercent > 0 && (
            <span className="subscription-view__toggle-badge">
              Save up to {savingsPercent}%
            </span>
          )}
        </div>
      </div>


      <section className="subscription-view__section">
        <h2 className="subscription-view__section-title">All plans</h2>
        {tiersLoading && !tiers && (
          <p className="subscription-view__hint">Loading available plans…</p>
        )}
        {tiersError && (
          <div className="subscription-view__error" role="alert">
            <AlertCircle size={20} aria-hidden="true" />
            <p>{tiersError.message || 'Unable to load plans'}</p>
            {tiersError.requestId && (
              <small className="subscription-view__request-id">
                ref: {tiersError.requestId}
              </small>
            )}
          </div>
        )}
        {tiers && tiers.length === 0 && !tiersLoading && (
          <p className="subscription-view__hint">
            No plans available yet — check back soon.
          </p>
        )}
        {tiers && tiers.length > 0 && (
          <TierCatalogGrid
            tiers={gridTiers}
            currentTierId={currentTier?.id}
            billingCycle={billingCycle}
            checkoutTierId={checkoutTierId}
            canManageEnterprise={canManageEnterprise}
            onSwitch={handleSwitchClick}
            onContact={handleContactClick}
            onManage={handleManageClick}
            onSubscribe={handleEnterpriseSubscribe}
          />
        )}
      </section>

      {/* Contact / Switch dialog */}
      {showContactDialog && (
        <div className="subscription-contact__overlay" onClick={closeContactDialog}>
          <div
            className="subscription-contact__modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={dialogTitle}
          >
            <div className="subscription-contact__head">
              <span className="subscription-contact__title">
                <span className="subscription-contact__icon">
                  <Crown size={16} aria-hidden="true" />
                </span>
                {dialogTitle}
              </span>
              <button
                type="button"
                className="subscription-contact__close"
                onClick={closeContactDialog}
                aria-label="Close dialog"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            {contactMode === 'switch' && (
              <p className="subscription-contact__lead">
                Self-serve plan switching is coming soon. Drop us a note
                and our team will help you migrate today.
              </p>
            )}

            <div className="subscription-contact__field">
              <label htmlFor="subscription-contact-email">
                {t('subscriptionView.dialog.fromLabel')}
              </label>
              <input
                id="subscription-contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('subscriptionView.dialog.placeholderEmail')}
              />
            </div>

            <div className="subscription-contact__field">
              <label htmlFor="subscription-contact-message">Message</label>
              <textarea
                id="subscription-contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={dialogPlaceholder}
                rows={3}
              />
            </div>

            {showNotification && (
              <p
                className={`subscription-contact__notification subscription-contact__notification--${notificationType}`}
                role="alert"
              >
                {notificationType === 'success' ? (
                  <Check size={16} aria-hidden="true" />
                ) : (
                  <AlertCircle size={16} aria-hidden="true" />
                )}
                <span>
                  {notificationType === 'success'
                    ? t('subscriptionView.dialog.sentSuccessfully')
                    : t('subscriptionView.dialog.somethingWentWrong')}
                </span>
                <button
                  type="button"
                  onClick={() => setShowNotification(false)}
                  aria-label="Dismiss notification"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </p>
            )}

            <div className="subscription-contact__actions">
              <button
                type="button"
                className="subscription-contact__cancel"
                onClick={closeContactDialog}
                disabled={loading}
              >
                {t('subscriptionView.dialog.cancel')}
              </button>
              <button
                type="button"
                className="subscription-contact__send"
                onClick={handleSendContact}
                disabled={loading || !message.trim() || !email.trim()}
              >
                {loading
                  ? t('subscriptionView.dialog.sending')
                  : t('subscriptionView.dialog.send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionView;
